/**
 * Bedrock 클라우드 계정 등록 마법사 (4단계)
 *   1. 기본 정보 (이름 · 인증 방식)
 *   2. 자격증명 (Assume Role: External ID + Role ARN / Access Key: Key ID + Secret)
 *   3. 리전 선택
 *   4. 검토
 */

import { useState, useEffect } from 'react';
import { Button, Modal, Select, useToast } from '../ui';
import { adminApi, CreateBedrockAccountInput, BedrockAuthType, ScopeOption } from '../../api/aiProvider';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  defaultScopeOverride?: string;
}

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia) · us-east-1' },
  { value: 'us-west-2', label: 'US West (Oregon) · us-west-2' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul) · ap-northeast-2' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) · ap-northeast-1' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt) · eu-central-1' },
];

const STEPS = ['기본 정보', '자격증명', '리전 선택', '검토'];

export function BedrockAccountModal({ isOpen, onClose, onSaved, defaultScopeOverride }: Props) {
  const { showToast } = useToast();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [authType, setAuthType] = useState<BedrockAuthType>('assumeRole');
  const [externalId, setExternalId] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState('ap-northeast-2');
  const [s3BucketName, setS3BucketName] = useState('');
  const [serviceAccountId, setServiceAccountId] = useState('');
  const [principalArn, setPrincipalArn] = useState('');

  // 등록 대상 소속 (팀/고객사/테넌트)
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([]);
  const [scopeValue, setScopeValue] = useState(defaultScopeOverride || '');

  // 모달 열릴 때 초기화 + External ID 발급 + 소속 선택지 로드
  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setName('');
    setAuthType('assumeRole');
    setRoleArn('');
    setAccessKeyId('');
    setSecretAccessKey('');
    setRegion('ap-northeast-2');
    setS3BucketName('');
    adminApi.getBedrockExternalId()
      .then((d) => { setExternalId(d.externalId); setServiceAccountId(d.serviceAccountId); setPrincipalArn(d.principalArn); })
      .catch(() => { setExternalId(''); setServiceAccountId(''); setPrincipalArn(''); });
    adminApi.getBedrockScopeOptions()
      .then((opts) => {
        setScopeOptions(opts);
        // defaultScopeOverride가 없을 때만 첫 번째 옵션으로 설정
        if (!defaultScopeOverride) setScopeValue(opts[0]?.value || '');
      })
      .catch(() => { setScopeOptions([]); if (!defaultScopeOverride) setScopeValue(''); });
  }, [isOpen]);

  const canNext = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) {
      return roleArn.trim().length > 0;
    }
    if (step === 2) return region.trim().length > 0;
    return true;
  };

  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    try {
      await adminApi.testBedrockCredentials({
        authType,
        region,
        roleArn: roleArn.trim(),
        externalId,
      });
      showToast('success', '연결이 정상입니다.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '연결 테스트 실패';
      showToast('error', msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const input: CreateBedrockAccountInput = {
        name: name.trim(),
        authType,
        region,
        ...(scopeValue ? { scopeOverride: scopeValue } : {}),
        roleArn: roleArn.trim(),
        externalId,
        ...(s3BucketName.trim() ? { s3BucketName: s3BucketName.trim().toLowerCase() } : {}),
      };
      await adminApi.createBedrockAccount(input);
      showToast('success', 'Bedrock 계정이 등록되었습니다.');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '등록에 실패했습니다.';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const principal = principalArn
    || `arn:aws:iam::${serviceAccountId || '<서비스-AWS-계정-ID>'}:root`;
  const trustPolicy = `{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "${principal}" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "${externalId}" } }
  }]
}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="클라우드 계정 등록"
      size="lg"
      bodyClassName="bedrock-modal-body"
      footerRight={
        <>
          <Button color="gray" appearance="outline" onClick={onClose}>취소</Button>
          {step > 0 && (
            <Button color="gray" appearance="outline" onClick={() => setStep((s) => s - 1)}>이전</Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button color="primary" onClick={() => setStep((s) => s + 1)} disabled={!canNext()}>다음</Button>
          ) : (
            <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? '등록 중...' : '등록'}</Button>
          )}
        </>
      }
    >
      {/* 단계 표시 */}
      <div className="bedrock-steps">
        {STEPS.map((label, i) => (
          <div key={label} className={`bedrock-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="bedrock-step-num">{i < step ? '✓' : i + 1}</span>
            <span className="bedrock-step-label">{label}</span>
          </div>
        ))}
      </div>

      {/* 1. 기본 정보 */}
      {step === 0 && (
        <div className="form-grid">
          <div className="form-item">
            <div className="form-label">Credential 이름 <span className="required">*</span></div>
            <div className="input">
              <input className="input-control" type="text" placeholder="예: 운영 Bedrock 계정"
                value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
          </div>
          {scopeOptions.length > 1 && !defaultScopeOverride && (
            <div className="form-item">
              <div className="form-label">소속 <span className="required">*</span></div>
              <Select value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}>
                {scopeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
          )}
          <div className="form-item">
            <div className="form-label">인증 방식 <span className="required">*</span></div>
            <div className="bedrock-auth-choice">
              <button type="button" className={`bedrock-auth-card ${authType === 'assumeRole' ? 'active' : ''}`}
                onClick={() => setAuthType('assumeRole')}>
                <strong>Assume Role <span className="bedrock-recommend">권장</span></strong>
                <span>비밀키 저장 없이 IAM Role 위임 방식으로 연결</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. 자격증명 - Assume Role (가이드 포함) */}
      {step === 1 && authType === 'assumeRole' && (
        <div className="bedrock-guide">
          {/* 1단계: External ID */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1단계: External ID 확인</div>
            <div className="bedrock-ext-row">
              <input className="input-control" type="text" value={externalId} readOnly />
              <button className="bedrock-copy-btn" onClick={() => { navigator.clipboard.writeText(externalId); showToast('success', 'External ID 복사됨'); }} title="복사">⧉</button>
            </div>
            <ul className="bedrock-guide-hint">
              <li>이 값은 자동 생성되었으며, 변경할 수 없습니다.</li>
              <li>External ID를 복사하여 다음 단계에서 AWS 콘솔에 붙여넣기 합니다.</li>
            </ul>
          </div>

          {/* 2단계: IAM Role 생성 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2단계: AWS IAM Role 생성</div>

            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>IAM 콘솔에서 역할 생성 시작</strong>
                <p>AWS Console → IAM → 역할(Roles) → 역할 생성(Create role)</p>
              </div>
            </div>

            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>신뢰할 엔터티 유형 선택</strong>
                <p>사용자 지정 신뢰 정책(Custom trust policy) 선택 — 아래 신뢰 정책을 복사하여 붙여넣기 하세요:</p>
                <div className="bedrock-code-wrap">
                  <button className="bedrock-copy-btn bedrock-code-copy" onClick={() => { navigator.clipboard.writeText(trustPolicy); showToast('success', '신뢰 정책 복사됨'); }} title="복사">⧉</button>
                  <pre className="bedrock-code">{trustPolicy}</pre>
                </div>
              </div>
            </div>

            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-3</span>
              <div>
                <strong>권한 정책 추가</strong>
                <p>권한 추가(Add permissions) → Use existing policy → 아래 정책 중 하나 선택</p>
                <ul className="bedrock-guide-hint">
                  <li><b>AmazonBedrockFullAccess</b> <span className="bedrock-recommend">권장</span> — Bedrock 모델 호출 권한</li>
                  <li>또는 Bedrock 모델 호출(bedrock:InvokeModel)이 가능한 커스텀 정책</li>
                  <li style={{ color: 'var(--warning)', marginTop: 4 }}><b>S3 업로드 사용 시 추가 필요</b> — <b>AmazonS3FullAccess</b> 또는 버킷 접근 커스텀 정책<br /><span style={{ fontSize: 11, color: 'var(--text-3)' }}>필요 권한: s3:PutObject, s3:GetObject, s3:DeleteObject, s3:CreateBucket, s3:ListBucket</span></li>
                </ul>
              </div>
            </div>

            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-4</span>
              <div>
                <strong>역할 이름 지정 및 생성 완료</strong>
                <p>역할 이름(Role name) 입력 → 역할 생성(Create role) 클릭 → 생성된 역할 상세에서 ARN 복사</p>
              </div>
            </div>
          </div>

          {/* 3단계: Role ARN 입력 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3단계: 생성한 Role의 ARN 입력</div>
            <div className="bedrock-ext-row">
              <input className="input-control" type="text" placeholder="arn:aws:iam::123456789012:role/MyBedrockRole"
                value={roleArn} onChange={(e) => setRoleArn(e.target.value)} />
              <Button color="primary" appearance="outline" onClick={handleTest} disabled={testing || !roleArn.trim()}>
                {testing ? '테스트 중...' : '연결 테스트'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 3. 리전 선택 */}
      {step === 2 && (
        <div className="form-grid">
          <div className="form-item">
            <div className="form-label">리전 <span className="required">*</span></div>
            <Select value={region} onChange={(e) => setRegion(e.target.value)}>
              {AWS_REGIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Select>
          </div>
          <div className="form-item">
            <div className="form-label">S3 버킷명 <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(선택)</span></div>
            <div className="input">
              <input
                className="input-control"
                type="text"
                placeholder="예: my-acxion-documents (소문자·숫자·하이픈만)"
                value={s3BucketName}
                onChange={(e) => setS3BucketName(e.target.value.toLowerCase())}
              />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>
              파일 업로드(지식 파이프라인, 스페이스)에 사용할 S3 버킷입니다.<br />
              버킷이 없으면 자동으로 생성됩니다. IAM 역할에 S3 권한이 있어야 합니다.
            </p>
          </div>
        </div>
      )}

      {/* 4. 검토 */}
      {step === 3 && (
        <div className="bedrock-review">
          <div className="bedrock-review-row"><span>이름</span><strong>{name}</strong></div>
          {scopeOptions.length > 1 && !defaultScopeOverride && (
            <div className="bedrock-review-row"><span>소속</span><strong>{scopeOptions.find((o) => o.value === scopeValue)?.label || '-'}</strong></div>
          )}
          <div className="bedrock-review-row"><span>인증 방식</span><strong>Assume Role</strong></div>
          <div className="bedrock-review-row"><span>리전</span><strong>{region}</strong></div>
          <div className="bedrock-review-row"><span>Role ARN</span><strong>{roleArn}</strong></div>
          <div className="bedrock-review-row"><span>External ID</span><strong>{externalId}</strong></div>
          {s3BucketName && <div className="bedrock-review-row"><span>S3 버킷명</span><strong>{s3BucketName}</strong></div>}
        </div>
      )}
    </Modal>
  );
}

export default BedrockAccountModal;
